import styled from "@emotion/styled";
import { memo, useEffect, useRef, useState } from "react";
import type { MarketShareDataPoint, MarketShareLineGroupProps } from "./types";

const MarketSharePathElement = styled.path`
  stroke-width: 3;
  fill: none;
`;

const circleMarkerRadius = 5;

const MarketShareCircleElement = styled.circle`
  r: ${circleMarkerRadius};
`;

const MarketShareLineGroup = ({
  sectorProductId,
  lineData,
  lineGenerator,
  pointGenerator,
  colorMap,
}: MarketShareLineGroupProps) => {
  const lineRef = useRef<SVGPathElement | null>(null);
  const [hasAnimated, setHasAnimated] = useState<boolean>(false);
  const [totalPathLength, setTotalPathLength] = useState<number>(0);
  const [animating, setAnimating] = useState<boolean>(false);

  let useColor = colorMap.get(sectorProductId);

  // Measure path length when lineGenerator changes
  useEffect(() => {
    if (lineRef.current) {
      const length = lineRef.current.getTotalLength();
      setTotalPathLength(length);
    }
  }, [lineGenerator]);

  // Trigger animation when path length is known
  useEffect(() => {
    if (totalPathLength > 0 && !hasAnimated) {
      // Start with full offset, then animate to 0
      setAnimating(true);
    }
  }, [totalPathLength, hasAnimated]);

  const strokeDasharray = totalPathLength > 0
    ? `${totalPathLength} ${totalPathLength}`
    : undefined;

  const strokeDashoffset = animating ? 0 : (hasAnimated ? 0 : totalPathLength);

  return (
    <g>
      <MarketSharePathElement
        d={lineGenerator(lineData)}
        stroke={useColor}
        ref={lineRef}
        fill="none"
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        style={{
          transition: animating && !hasAnimated
            ? "stroke-dashoffset 750ms cubic-bezier(0.455, 0.03, 0.515, 0.955)"
            : undefined,
        }}
        onTransitionEnd={() => {
          if (!hasAnimated) {
            setHasAnimated(true);
            setAnimating(false);
          }
        }}
      />
      {lineData
        .filter((productYear: MarketShareDataPoint) =>
          productYear.globalMarketShare !== undefined &&
          productYear.globalMarketShare !== null
        )
        .map((productYear: MarketShareDataPoint) => {
          let { year, globalMarketShare: value } = productYear;

          let { cx, cy } = pointGenerator({ year, value });

          return (
            <MarketShareCircleElement
              key={`${sectorProductId}-${year}`}
              cx={cx}
              cy={cy}
              fill={useColor}
              r={circleMarkerRadius}
            />
          );
        })}
    </g>
  );
};

export default memo(MarketShareLineGroup);
